import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import { useMediaQuery } from 'react-responsive';

import { Table, TextField, Space, TableCardMobile } from '~/components';
import { useGetApplication } from '~/controllers/wizard';
import { hasAtLeastOneWarningField } from '~/utils/softDeclinesFields';
import EditButton from '~/components/views/ShareholdersMultiple/EditButton';
import theme from '~/constants/theme';
import RelationField from '~/components/fields/RelationField';
import WithPep from '~/components/WithPep';
import { Draft } from '~/types/Draft';
import classNames from 'classnames';
import { Application } from '~/types/Application';
import { managersGetFormName } from '~/pages/ksa/application/managers/components/ManagerEdit';
import { CheckThinIcon } from '~/img';
import { MinusOutlined } from '@ant-design/icons';
import longdash from '~/constants/longdash';
import colors from '~/constants/colors';
import { isCurrentUserByEmail } from '~/utils/getDecodedJwt';
import { WithCurrentUserIcon } from '~/components/CurrentUser';

export type ManagerRowType = {
  uid?: string;
  fullName?: string;
  hasPassport?: boolean;
  email?: string;
  idNumber?: string;
  relation?: string;
  isAuthorizedSignatory?: boolean;
  isPep?: boolean;
  onEdit?: () => void;
};

interface Props {
  managers: ManagerRowType[];
}

const ManagersTable: FC<Props> = (props) => {
  const { managers } = props;
  const { t } = useTranslation('ksaApplication');

  const applicationQuery = useGetApplication();

  const managersColumns: ColumnsType<ManagerRowType> = useMemo(
    () => [
      {
        title: t('Full name'),
        dataIndex: 'fullName',
        key: 'fullName',
        render: (fullName: boolean, record: any) => {
          const isCurrentUser = isCurrentUserByEmail(record?.email);
          return <WithCurrentUserIcon showLabel={isCurrentUser}>{fullName}</WithCurrentUserIcon>;
        },
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon style={checkIconStyle} />;
          return <MinusOutlined style={checkIconStyle} />;
        },
      },
      {
        title: t('ID number'),
        dataIndex: 'idNumber',
        key: 'idNumber',
        render: (idNumber?: string) => idNumber || longdash,
      },

      {
        title: t('Signatory'),
        dataIndex: 'isAuthorizedSignatory',
        key: 'isAuthorizedSignatory',
        render: (isAuthorizedSignatory: boolean) => {
          if (isAuthorizedSignatory) return t('Yes');
          return t('No');
        },
      },

      {
        title: '',
        key: 'action',
        // fixed: 'right',
        render: (_, record) => (
          <Space size={16} style={actionsStyle}>
            <EditButton id="managersTable_EditButton" onClick={record.onEdit} />
          </Space>
        ),
      },
    ],
    [t]
  );

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });

  return (
    <Space size={24} direction="vertical">
      {!isEmpty(managers) && (
        <>
          {!isTablet ? (
            <Table
              rowClassName={(_, index) =>
                classNames({
                  'ant-table-row-warning': hasAnyActiveWarning(applicationQuery.data, index),
                })
              }
              id="managersTable"
              columns={managersColumns}
              dataSource={managers}
              loading={applicationQuery.isLoading}
              pagination={pagination}
              rowKey="uid"
            />
          ) : (
            <Space size={12} direction="vertical">
              {managers?.map((item, index) => (
                <TableCardMobile
                  fields={getManagerCardContent(item)}
                  key={item.uid}
                  hasValidationWarnings={hasAnyActiveWarning(applicationQuery.data, index)}
                  onEdit={item.onEdit}
                />
              ))}
            </Space>
          )}
        </>
      )}
    </Space>
  );
};

const getManagerCardContent = (record: ManagerRowType) => {
  const t = i18next.getFixedT(null, 'ksaApplication');
  const { fullName, hasPassport, idNumber, relation, isPep, isAuthorizedSignatory, email } =
    record ?? {};
  const isCurrentUser = isCurrentUserByEmail(email);

  return [
    <TextField key="fullName" label={t('Full Name')}>
      <WithCurrentUserIcon showLabel={isCurrentUser}>{fullName}</WithCurrentUserIcon>
    </TextField>,
    <TextField key="hasPassport" label={t('Passport')}>
      {hasPassport ? (
        <CheckThinIcon style={checkIconStyle} />
      ) : (
        <MinusOutlined style={checkIconStyle} />
      )}
    </TextField>,
    <TextField key="idNumber" label={t('Document number')}>
      {idNumber}
    </TextField>,
    <TextField key="relation" label={t('Relation')}>
      <RelationField value={relation} />
    </TextField>,
    <TextField key="signatory" label={t('Signatory')}>
      <>{isAuthorizedSignatory ? t('Yes') : t('No')}</>
    </TextField>,
    <TextField key="pep" label={t('PEP')}>
      <>{isPep ? t('Yes') : t('No')}</>
    </TextField>,
  ];
};

export const getManagersData = (
  managersDraft: Draft<'managers'> | undefined,
  openEdit: (uid: string) => void
): ManagerRowType[] => {
  const managers = managersDraft?.managers?.managers;
  return (
    managers?.map((item: any) => {
      return {
        uid: item.uid,
        fullName: item?.personalData?.fullName,
        email: item?.personalData?.email,
        hasPassport: !isEmpty(item?.passport?.documentNumber),
        idNumber: item?.personalDocument?.idNumber,
        relation: item?.personalData?.relation,
        isAuthorizedSignatory: item?.personalData?.isAuthorizedSignatory,
        isPep: item?.personalData?.isPep,
        onEdit: () => item?.uid && openEdit(item.uid),
      };
    }) || []
  );
};

const hasAnyActiveWarning = (application: Application | undefined, index: number) =>
  hasAtLeastOneWarningField(application, managersGetFormName(index).join('.'));

const pagination = { hideOnSinglePage: true };
const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };
const checkIconStyle = { color: colors.blue['1100'] };

export default ManagersTable;
